/**
 * haloPresentation — the single presentation-state derivation shared by
 * QorynHalo and StateLabel. Root corrections (2026-09-25):
 *
 *   • Every active presentation (`listening` / `muted` / `thinking` /
 *     `speaking` / `speaking_muted`) MUST be gated on ALL of:
 *         - `connectionState === "open"` (nothing else counts as live)
 *         - `audioMode === "live"` (text-only cannot host audio-reactive
 *           states)
 *         - `!held` (the preview HELD overlay is preserved during
 *           adoption; when held, no active presentation may leak)
 *   • Stale speaking / thinking events MUST NOT survive closed / error
 *     / held / text-only.
 *   • `connecting` is a distinct state — never treated as listening.
 *   • Guard ORDER is: held → error → not-open → not-live-audio → then
 *     agent-state resolution. Reversing this order (as the prior
 *     implementation did) allowed thinking/speaking to slip past the
 *     connection guard.
 *
 * The derivation accepts an optional `held` boolean so that when this
 * branch is adopted into `/app` (which carries the `heldOffline`
 * overlay), the same code preserves the truthful "held" presentation.
 * On this branch the hook does not yet emit `held`; the caller passes
 * `undefined` and the derivation treats it as `false` — no runtime
 * change until adoption wires the flag.
 */

const KNOWN_AGENT_STATES = new Set([
    "idle",
    "connecting",
    "listening",
    "thinking",
    "speaking",
    "error",
]);

// Only "open" counts as a live connection — per the inspected
// `useRealtimeSession` hook contract on `main` (state moves through
// `new → connecting → open` and the SDK emits `"open"` when the RTC
// connection succeeds). No other value is treated as live; the prior
// implementation's `"connected"` fallback was invented compatibility
// with no source basis and has been removed.
const OPEN_STATES = new Set(["open"]);

export function derivePresentation({
    agentState = "idle",
    connectionState = "closed",
    micMuted = false,
    outputMuted = false,
    audioMode = "live",
    held = false,
} = {}) {
    // 1. HELD dominates everything. The overlay tile must be preserved
    //    regardless of any stale agentState/connectionState combo.
    if (held) return "held";

    // 2. Error dominates the remaining states so a session error can't
    //    be masked by a stale "speaking" event that has not yet been
    //    cleaned up by the SDK.
    if (agentState === "error") return "error";

    // 3. Explicit connecting state — before any live-only presentation.
    if (agentState === "connecting" || connectionState === "connecting") {
        return "connecting";
    }

    // 4. From here on, ANY active presentation requires an open
    //    connection AND live audio mode. Text-only or a not-yet-open
    //    transport downgrades to "text_only" or "idle" respectively.
    const isOpen = OPEN_STATES.has(connectionState);
    if (!isOpen) return "idle";
    if (audioMode !== "live") return "text_only";

    // 5. Only now do we honour agentState — with a known-set filter so
    //    a rogue string cannot render as a live presentation.
    const a = KNOWN_AGENT_STATES.has(agentState) ? agentState : "idle";

    if (a === "thinking") return "thinking";
    if (a === "speaking") {
        // Speaker mute settles the reactive motion and yields a
        // truthful "silently" label. Captions keep flowing on the
        // compact caption strip and in the right-column transcript.
        return outputMuted ? "speaking_muted" : "speaking";
    }

    // agentState is "listening" or "idle" here.
    if (a === "listening") return micMuted ? "muted" : "listening";
    return "idle";
}

export function isAudioReactive(presentation) {
    // Only genuinely-audible speaking is audio-reactive. Speaker-muted
    // speech, text-only, held, and every other state must never react
    // to analyser data.
    return presentation === "speaking";
}

export function isLivePresentation(presentation) {
    return (
        presentation === "listening" ||
        presentation === "muted" ||
        presentation === "thinking" ||
        presentation === "speaking" ||
        presentation === "speaking_muted"
    );
}

// Q asset opacity map. Consumed by the Halo center layer and any
// diagnostic overlay so both see the same numbers.
export const Q_OPACITY_BY_PRESENTATION = {
    idle: 1.0,
    connecting: 0.85,
    listening: 0.85,
    muted: 0.75,
    thinking: 0.9,
    speaking: 0.75,
    speaking_muted: 0.8,
    text_only: 0.85,
    held: 0.7,
    error: 0.65,
};

// Presentation set surfaced by the derivation. Kept in one place so the
// StateLabel copy map and the Halo behavior maps can be audited against
// a single source of truth.
export const ALL_PRESENTATIONS = Object.freeze([
    "idle",
    "connecting",
    "text_only",
    "held",
    "listening",
    "muted",
    "thinking",
    "speaking",
    "speaking_muted",
    "error",
]);

export default derivePresentation;
